import { partition, length, curry } from 'ramda'
import React from 'react'

import { FlightLabelStatus } from '../../../../src/ui/labels/FlightLabel.jsx'
import { StateObjMultiple } from '../../../../src/utils/stateObj.js'
import { FlightType } from '../../../flightStatus/src/flightDetailsMapper.js'

import { FlightSearchHeader } from './FlightSearchHeader/FlightSearchHeader.jsx'
import { FlightSearch, ListItemType } from './FlightSearchView/FlightSearchView.jsx'

const createListItem = curry((type, value) => ({ type, value }))

// Commands to be registered for the JS SDK (if the sdkServer is listening)
const sdkCommands = [
  {
    command: 'flightSetSearch',
    args: [
      { name: 'searchTerm', type: 'string' }
    ]
  }
]

export function create (app, config) {
  const init = () => {
    app.bus.send('search/registerSpecialQuery', { term: 'arrivals', event: 'flightSearch/show', params: { flightType: FlightType.ARRIVAL } })
    app.bus.send('search/registerSpecialQuery', { term: 'departures', event: 'flightSearch/show', params: { flightType: FlightType.DEPARTURE } })
    app.bus.send('search/registerSpecialQuery', { term: 'gate.departures', event: 'flightSearch/show', params: { flightType: FlightType.DEPARTURE } })
    app.bus.send('search/registerSpecialQuery', { term: 'flight status', event: 'flightSearch/show', params: { flightType: FlightType.DEPARTURE } })

    sdkCommands.forEach(cdef => app.bus.send('clientAPI/registerCommand', cdef)) // register our custom JS SDK commands
    app.bus.on(`clientAPI/flightSetSearch`, ({ searchTerm }) => onSearchTermChange(searchTerm))
  }

  const FLIGHT_SEARCH_HEADER_ID = 'flightSearchHeader'
  const FLIGHT_SEARCH_VIEW_ID = 'flightSearchView'

  let firstTime = true // firstTime flag hack to handle deepLinking restoration

  const defaultWidgetState = {
    currentFlightType: FlightType.DEPARTURE,
    flightItems: [],
    searchTerm: '',
    noResultsError: false,
    apiError: false,
    logoUrl: null
  }
  const widgetState = new StateObjMultiple(defaultWidgetState)
  widgetState.addCallback(state => {
    app.bus.send('deepLinking/notifyState', {
      id: 'online/flightDetailsSearch',
      type: state.currentFlightType,
      searchTerm: state.searchTerm,
      flightId: state.flightId
    })
  })

  app.bus.send('layers/register', {
    id: FLIGHT_SEARCH_HEADER_ID,
    widget: () => <FlightSearchHeader
      widgetState={widgetState}
      onSearchTermChange={onSearchTermChange}
      onFlightTypeSwitched={onFlightTypeSwitched}
      logoUrl={widgetState.getState().logoUrl}
      clientName={app.config.name}
      logoLinkUrl={app.config.logoLinkUrl}
      onBackButtonClicked={onBackButtonClicked}
      isDesktop={app.env.isDesktop()}
      T={app.gt()}
    />,
    layoutId: 'headerOnline',
    layoutName: 'fullscreen'
  })

  app.bus.send('layers/register', {
    id: FLIGHT_SEARCH_VIEW_ID,
    widget: () => <FlightSearch
      widgetState={widgetState}
      onSearchTermChange={onSearchTermChange}
      onFlightClick={showFlightById}
      onFlightTypeSwitched={onFlightTypeSwitched}
      isDesktop={app.env.isDesktop()}
      T={app.gt()}
    />,
    layoutId: 'content',
    layoutName: 'fullscreen'
  })

  function onSearchTermChange (searchTerm) {
    widgetState.update({ searchTerm })
    updateFlightItems()
  }

  app.bus.on('flightSearch/setSearch', ({ searchTerm }) => onSearchTermChange(searchTerm))

  const showFlightById = flightId => {
    widgetState.update({ flightId })
    app.bus.send('flightDetails/show', { flightId })
  }

  const onBackButtonClicked = () => {
    app.bus.send('headerOnline/show')
    app.bus.send('search/customAction', { name: null, category: null })
  }

  app.bus.on('flightSearch/show', async ({ flightType = null } = {}) => {
    const initialWidgetState = flightType ? { defaultWidgetState, currentFlightType: flightType } : defaultWidgetState

    if (firstTime) {
      if (config.deepLinkProps) {
        if (config.deepLinkProps.type)
          initialWidgetState.currentFlightType = config.deepLinkProps.type
        if (config.deepLinkProps.searchTerm)
          initialWidgetState.searchTerm = config.deepLinkProps.searchTerm
        if (config.deepLinkProps.flightId)
          setTimeout(() => showFlightById(config.deepLinkProps.flightId), 0) // after thread finishes, call this
      }
      firstTime = false // only do this once
    }

    widgetState.update(initialWidgetState)

    await app.bus.get('theme/getVenueLogo')
      .then(venueLogoUrl => widgetState.update({ logoUrl: venueLogoUrl }))

    app.bus.send('layers/show', { id: FLIGHT_SEARCH_HEADER_ID })
    app.bus.send('layers/show', { id: FLIGHT_SEARCH_VIEW_ID })
    app.bus.send('map/cleanMap')
    app.bus.send('history/register', { viewId: FLIGHT_SEARCH_VIEW_ID, event: 'flightSearch/show' })
    updateFlightItems()
  })

  function onFlightTypeSwitched (currentFlightType) {
    widgetState.update({ currentFlightType })
    updateFlightItems()
  }

  async function updateFlightItems () {
    const { currentFlightType, searchTerm } = widgetState.getState()
    widgetState.update({ loading: true })
    const { flights, apiError } = await app.bus.get('flightStatus/query', { term: searchTerm, type: currentFlightType })
    widgetState.update({ loading: false })

    const uiFlights = await app.bus.get('flightStatus/mapFlightListItems', { flights })
    const flightItems = splitAndWrapInListItems(currentFlightType, uiFlights)

    const noResultsError = !length(flightItems)
    widgetState.update({ flightItems, noResultsError, apiError })
  }

  function splitAndWrapInListItems (currentFlightType, uiFlights) {
    const splitPassedAndActiveFlights = partition(({ status }) =>
      status === FlightLabelStatus.DEPARTED || status === FlightLabelStatus.ARRIVED)
    const [passed, active] = splitPassedAndActiveFlights(uiFlights)

    const items = []
    items.push(...active.map(createListItem(ListItemType.FLIGHT)))
    if (length(passed)) {
      const flightTypeName =
        currentFlightType === FlightType.DEPARTURE ? 'Departures' : 'Arrivals'
      const sectionTitle = app.gt()(`flightDetails:Previous ${flightTypeName}`)
      items.push(createListItem(ListItemType.SECTION_TITLE, sectionTitle))
      items.push(...passed.map(createListItem(ListItemType.FLIGHT)))
    }
    return items
  }

  const runTest = async (initialState, testRoutine) => {
    widgetState.update({ ...initialState })
    await testRoutine()
    return widgetState.getState()
  }

  return {
    init,
    runTest,
    internal: {
      onFlightTypeSwitched,
      onSearchTermChange
    }
  }
}
